import { useRef } from 'react'
import { useFormContext, useFormState } from 'react-hook-form'

import { Box } from '@factorialco/deprecated-design-system'
import { CurrencySelectorCurrencyFragment } from 'generated/resources/graphql'

import { useEffect } from 'modules/core/hooks/useEffect'
import i18n from 'modules/core/lib/i18n'

import { useExchangeRate } from 'modules/expenses/hooks/useExchangeRate'
import { normalizeEffectiveOn } from 'modules/expenses/hooks/useExpensable'
import { isReimbursableFieldEnabled } from 'modules/expenses/hooks/useExpensesCustomization'

import { CustomizationProps } from '../../withCustomizationFields'
import { PermissionsProps } from '../../withFieldPermissions'
import PaymentMethodField from './PaymentMethodField'
import PaymentSwitch from './PaymentSwitch'
import ReimbursableAmountField from './ReimbursableAmountField'
import ReimbursableCurrencyField from './ReimbursableCurrencyField'

interface Props extends PermissionsProps, CustomizationProps {
  currencies?: CurrencySelectorCurrencyFragment[]
  canCustomizeSettings: boolean
  isAutomaticExpensable?: boolean
}

const PaymentInformation = (props: Props) => {
  const { isAutomaticExpensable, customizationFields } = props
  const { watch, setValue, setError, trigger } = useFormContext()
  const { dirtyFields } = useFormState()
  const {
    amount,
    currency,
    reimbursableCurrency,
    effectiveOn,
    reimbursableAmount,
    payment: selectedPayment,
  } = watch()
  const reimbursablePayment = selectedPayment === 'reimbursable' && !isAutomaticExpensable
  const showPayment = !isAutomaticExpensable

  const amountIsDirty = !!dirtyFields.amount

  const exchangeRateEnabled = isReimbursableFieldEnabled(
    customizationFields,
    selectedPayment,
    'exchange_rate'
  )
  const isReimbursableAmountFieldEnabled = isReimbursableFieldEnabled(
    customizationFields,
    selectedPayment,
    'reimbursable_amount'
  )

  const { fetch: fetchExchangeRate, loading: loadingExchangeRate } = useExchangeRate({
    onCompleted: ({ rate }) => {
      if (!isReimbursableAmountFieldEnabled) return
      if (amount) {
        setValue('reimbursableAmount', parseFloat((amount * rate).toFixed(2)), {
          shouldDirty: true,
          shouldValidate: true,
        })
        setValue('exchangeRate', rate, { shouldDirty: true })
        trigger(['reimbursableAmount', 'exchangeRate'])
      }
    },
    onError: () => {
      if (!isReimbursableAmountFieldEnabled) return
      setValue('reimbursableAmount', null, { shouldDirty: true })
      setError(
        'reimbursableAmount',
        {
          type: 'custom',
          message: i18n.t('expenses_v2.forms.errors.cannot_calculate_exchange_rate'),
        },
        { shouldFocus: true }
      )
      trigger('reimbursableAmount')
    },
  })

  // Use ref to store fetchExchangeRate and have an stable reference
  const fetchExchangeRateRef = useRef(fetchExchangeRate)
  fetchExchangeRateRef.current = fetchExchangeRate

  const normalizedEffectiveOn = effectiveOn ? normalizeEffectiveOn(effectiveOn) : null

  // Handle same currency case - no exchange rate needed
  useEffect(() => {
    if (!isReimbursableAmountFieldEnabled) return
    if (!amount || !currency || !reimbursableCurrency) return
    if (currency === reimbursableCurrency && !reimbursableAmount) {
      setValue('reimbursableAmount', amount, { shouldDirty: true, shouldValidate: true })
      setValue('exchangeRate', 1, { shouldDirty: true, shouldValidate: true })
      trigger(['reimbursableAmount', 'exchangeRate'])
    }
  }, [
    currency,
    reimbursableCurrency,
    amount,
    reimbursableAmount,
    isReimbursableAmountFieldEnabled,
    setValue,
    trigger,
  ])

  // Handle fetch exchange rate
  useEffect(() => {
    if (!amountIsDirty) return
    if (!isReimbursableAmountFieldEnabled) return
    if (!normalizedEffectiveOn || !amount || !currency || !reimbursableCurrency) return
    if (!exchangeRateEnabled) return

    if (currency !== reimbursableCurrency) {
      fetchExchangeRateRef.current({
        baseCurrency: currency,
        targetCurrency: reimbursableCurrency,
        date: normalizedEffectiveOn,
      })
    }
  }, [
    currency,
    reimbursableCurrency,
    normalizedEffectiveOn,
    amount,
    isReimbursableAmountFieldEnabled,
    exchangeRateEnabled,
    amountIsDirty,
  ])

  return (
    <Box gap="s24">
      {showPayment && <PaymentSwitch {...props} fieldIdentifier="payment" />}
      <PaymentMethodField
        {...props}
        selectedPayment={selectedPayment}
        fieldIdentifier="payment_method"
      />
      {reimbursablePayment && (
        <Box gap="s24">
          <ReimbursableCurrencyField
            {...props}
            fieldIdentifier="reimbursable_currency"
            exchangeRateEnabled={exchangeRateEnabled}
            loadingExchangeRate={loadingExchangeRate}
          />
          <ReimbursableAmountField
            {...props}
            fieldIdentifier="reimbursable_amount"
            loadingExchangeRate={loadingExchangeRate}
          />
        </Box>
      )}
    </Box>
  )
}

export default PaymentInformation
